import { useSelector } from 'react-redux';
import { Box } from '@chakra-ui/react';
import { ApplicationState } from '../../../../../store';
import { CustomHeading } from 'shared-components';
import { CustomCollapse } from '../../../../CustomCollapse/CustomCollapse';
import Field from '../../../../Field/Field';
import { PrevNextButtons } from '../../../../PrevNextButtons';
import { FileList } from '../../../../FileList/FileList';

import { IDType, IOtherName } from 'types';
import { useNavigate } from 'react-router-dom';
import ApplicantHeader from '../../../../../components/molecules/applicantHeader';
import { SectionHeader, SectionTitles } from '@/components/molecules/SectionHeader';
import { getApplicationData } from '@/components/pages/applicant-dashboard/sections/helpers/getApplicationData';
import { combineUnevenArrays } from '../helpers';
import { formatYearMonthDate } from '@/utilities/dateHelpers';
import { useTaskNavigation } from '@/hooks/useTaskNavigation';
import { useTranslation } from "react-i18next";

export default function Identity() {
    const { t } = useTranslation('common');
  const navigate = useNavigate();
  const { application, identityDocuments } = useSelector((state: ApplicationState) => state.apply);
  const { nextRoute, previousRoute } = useTaskNavigation();

  const { initial, current } = getApplicationData(application);
  const { idType, givenName, gender, middleName, familyName, passport, otherNames } = current;
  const {
    idType: initialIdType,
    givenName: initialGivenName,
    gender: initialGender,
    middleName: initialMiddleName,
    familyName: initialFamilyName,
    passport: initialPassport,
    otherNames: initialOtherNames,
  } = initial;

  const showMiddleName = middleName || initialMiddleName;
  const showPassport = idType === IDType.Passport || initialIdType === IDType.Passport;
  const hasOtherNames =
    Boolean(Object.keys(otherNames ?? {}).length) || Boolean(Object.keys(initialOtherNames ?? {}).length);

  const otherNamesArr = Object.values(otherNames || {}) as IOtherName[];
  const initialOtherNamesArr = Object.values(initialOtherNames || {}) as IOtherName[];
  const combinedOtherNames = combineUnevenArrays<IOtherName>(otherNamesArr, initialOtherNamesArr);

  return (
    <>
      <ApplicantHeader pageName="Application" />
      <Box>
        <SectionHeader title={SectionTitles.IDENTITY} />

        <FileList files={identityDocuments || []} />

        <CustomHeading level="h2" mb={2} mt={6} fontSize="md">
          {t('common:data')}
                          </CustomHeading>

        <Box>
          <Field leftLabel="ID type" value={idType} originalValue={initialIdType} />
          <Field leftLabel="Given name" value={givenName} originalValue={initialGivenName} />
          {showMiddleName && <Field leftLabel="Middle name" value={middleName} originalValue={initialMiddleName} />}
          <Field leftLabel="Family name" value={familyName} originalValue={initialFamilyName} />
          <Field leftLabel="Legal Sex" value={gender} originalValue={initialGender} />
          {showPassport && (
            <>
              <Field
                leftLabel="Passport number"
                value={passport?.passportNo}
                originalValue={initialPassport?.passportNo}
              />
              <Field
                leftLabel="Passport issue date"
                value={formatYearMonthDate(passport?.dateOfIssue)}
                originalValue={formatYearMonthDate(initialPassport?.dateOfIssue)}
              />
              <Field
                leftLabel="Passport expiry"
                value={formatYearMonthDate(passport?.dateOfExpiry)}
                originalValue={formatYearMonthDate(initialPassport?.dateOfExpiry)}
              />
              <Field
                leftLabel="Passport issuing country"
                value={passport?.passportIssuingAuthority}
                originalValue={initialPassport?.passportIssuingAuthority}
              />
            </>
          )}
          {hasOtherNames && (
            <CustomCollapse key={`other-names-dropdown`} defaultIsOpen>
              <CustomCollapse.Trigger>{t('common:otherNames')}</CustomCollapse.Trigger>
              <CustomCollapse.Content>
                {combinedOtherNames.map((otherName, index) => {
                  const showOtherMiddleName = otherName.current?.middleName || otherName.original?.middleName;
                  const heading = `${otherName.current?.givenName ?? otherName.original?.givenName} ${
                    otherName.current?.familyName ?? otherName.original?.familyName
                  }`;
                  return (
                    <CustomCollapse
                      key={`${otherName.current?.reason ?? otherName.original?.reason}-${index}`}
                      defaultIsOpen
                    >
                      <CustomCollapse.Trigger>{heading}</CustomCollapse.Trigger>
                      <CustomCollapse.Content>
                        <Field
                          leftLabel="Given name"
                          value={otherName.current?.givenName}
                          originalValue={otherName.original?.givenName}
                        />
                        {showOtherMiddleName && (
                          <Field
                            leftLabel="Middle name"
                            value={otherName.current?.middleName}
                            originalValue={otherName.original?.middleName}
                          />
                        )}
                        <Field
                          leftLabel="Family name"
                          value={otherName.current?.familyName}
                          originalValue={otherName.original?.familyName}
                        />
                        <Field
                          leftLabel="Reason for name change"
                          value={otherName.current?.reason}
                          originalValue={otherName.original?.reason}
                        />
                      </CustomCollapse.Content>
                    </CustomCollapse>
                  );
                })}
              </CustomCollapse.Content>
            </CustomCollapse>
          )}
        </Box>

        <Box mt={6}>
          <PrevNextButtons
            onPrevious={previousRoute ? () => navigate(previousRoute) : () => navigate('../sections')}
            onNext={nextRoute ? () => navigate(nextRoute) : undefined}
          />
        </Box>
      </Box>
    </>
  );
}
